import type { SafeResponse } from './types';

export type SafeInvokeOptions<T, E = Error> = {
  onSuccess?: (data: T) => void;
  onError?: (error: E) => void;
  onFinally?: () => void;
};

/**
 * Wraps a promise or function in a try-catch block and returns a result object with a discriminated union.
 * Works with both synchronous and asynchronous functions.
 *
 * @param fn - Promise, async function, or sync function to invoke
 * @param options - Optional callbacks for success, error, and finally
 * @returns The result of the invocation (synchronous for sync functions, Promise for async)
 *
 * @example
 * // Sync function
 * const result = safeInvoke(() => JSON.parse('{"valid": true}'));
 * if (result.error) console.error(result.error);
 *
 * @example
 * // Async function
 * const result = await safeInvoke(async () => fetch('/api/data'));
 * if (result.error) console.error(result.error);
 *
 * @example
 * // Direct Promise
 * const result = await safeInvoke(fetch('/api/data'));
 * if (result.error) console.error(result.error);
 */
export function safeInvoke<T, E = Error>(
  fn: Promise<T>,
  options?: SafeInvokeOptions<T, E>,
): Promise<SafeResponse<T, E>>;
export function safeInvoke<T, E = Error>(
  fn: () => Promise<T>,
  options?: SafeInvokeOptions<T, E>,
): Promise<SafeResponse<T, E>>;
export function safeInvoke<T, E = Error>(fn: () => T, options?: SafeInvokeOptions<T, E>): SafeResponse<T, E>;
export function safeInvoke<T, E = Error>(
  fn: Promise<T> | (() => Promise<T>) | (() => T),
  options?: SafeInvokeOptions<T, E>,
): Promise<SafeResponse<T, E>> | SafeResponse<T, E> {
  try {
    const result = typeof fn === 'function' ? fn() : fn;

    if (result instanceof Promise) {
      return result
        .then((data) => {
          options?.onSuccess?.(data);
          return { data, error: null } as SafeResponse<T, E>;
        })
        .catch((error) => {
          options?.onError?.(error as E);
          return { data: null, error: error as E } as SafeResponse<T, E>;
        })
        .finally(() => {
          options?.onFinally?.();
        });
    }

    options?.onSuccess?.(result);
    options?.onFinally?.();
    return { data: result, error: null };
  } catch (error) {
    options?.onError?.(error as E);
    options?.onFinally?.();
    return { data: null, error: error as E };
  }
}
